import { proxy } from 'valtio'
import { createStoreContext, useStoreContext } from '../../shared/lib/hooks.ts'
import { ChatApi } from '../../shared/api/chatApi.ts'
import type { AppLocalStorage } from '../../shared/lib/appLocalStorage.ts'

export type VoiceSessionStage =
  | 'idle'
  | 'connecting'
  | 'active'
  | 'ended'
  | 'error'

interface LiveSessionState {
  stage: VoiceSessionStage
  chatId: number | null
  sessionStartTs: number | null
  isEndedFromIframe: boolean
  timerSeconds: number
  iframeLoading: boolean
  iframeError: boolean
  programTag: string | null
}

export class LiveSessionStore {
  state: LiveSessionState
  private timerInterval: ReturnType<typeof setInterval> | null = null

  constructor(
    private chatApi: ChatApi,
    private appStorage: AppLocalStorage,
  ) {
    this.state = proxy<LiveSessionState>({
      stage: 'idle',
      chatId: null,
      sessionStartTs: null,
      isEndedFromIframe: false,
      timerSeconds: 0,
      iframeLoading: true,
      iframeError: false,
      programTag: null,
    })
  }

  setIframeLoaded() {
    this.state.iframeLoading = false
    this.state.iframeError = false
  }

  setIframeError() {
    this.state.iframeLoading = false
    this.state.iframeError = true
  }

  private startTimer(fromTs: number) {
    this.stopTimer()
    this.state.timerSeconds = 0
    this.timerInterval = setInterval(() => {
      if (this.state.sessionStartTs) {
        const elapsed = Math.floor(
          (Date.now() - this.state.sessionStartTs) / 1000,
        )
        this.state.timerSeconds = elapsed
      }
    }, 1000)
  }

  private stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  async handleCallStarted(
    userId: number,
    programTag: string,
    timestamp: number,
  ) {
    try {
      this.state.stage = 'connecting'
      console.log('handleCallStarted', userId, programTag, timestamp)
      const courseId = this.appStorage.get('currentCourse')?.id
      if (courseId == null) throw new Error('No course is selected')
      const chat = await this.chatApi.chat_create({
        user: userId,
        program_tag: programTag,
        chat_title: programTag,
        language: 'EN',
        parameters: { course_id: courseId },
      })

      this.state.chatId = chat.id
      this.state.sessionStartTs = timestamp
      this.state.isEndedFromIframe = false
      this.state.programTag = programTag
      this.state.stage = 'active'
      this.startTimer(timestamp)
    } catch (error) {
      console.error('[LIVE SESSION STORE] Failed to create chat:', error)
      this.state.stage = 'error'
    }
  }

  async handleCallEndedFromIframe(durationSec: number | undefined) {
    if (!this.state.chatId) return
    this.state.isEndedFromIframe = true
    const duration = durationSec ?? 0
    try {
      await this.chatApi.updateVoiceLimit(this.state.chatId, { duration })
      if (this.state.programTag) {
        const courseId = this.appStorage.get('currentCourse')?.id
        await this.chatApi.userProgramComplete(this.state.programTag, courseId)
      }
    } catch (error) {
      console.error('[LIVE SESSION STORE] Failed to update voice limit:', error)
    } finally {
      this.finishSession()
    }
  }

  async handleCallEndedByUser(durationSec: number | undefined) {
    if (!this.state.chatId) return
    const duration = durationSec ?? 0
    try {
      await this.chatApi.updateVoiceLimit(this.state.chatId, { duration })
    } catch (error) {
      console.error(
        '[LIVE SESSION STORE] Failed to update voice limit (by user):',
        error,
      )
    }
  }

  async handleFallbackEnd() {
    // если уже пришёл ивент из iframe — ничего не делаем
    if (this.state.isEndedFromIframe) return
    if (!this.state.chatId || !this.state.sessionStartTs) return
    const duration = Math.floor((Date.now() - this.state.sessionStartTs) / 1000)
    try {
      await this.chatApi.updateVoiceLimit(this.state.chatId, { duration })
    } catch (error) {
      console.error(
        '[LIVE SESSION STORE] Failed to update voice limit (fallback):',
        error,
      )
    } finally {
      this.finishSession()
    }
  }

  private finishSession() {
    this.stopTimer()
    this.state.stage = 'ended'
    this.state.chatId = null
    this.state.sessionStartTs = null
    this.state.timerSeconds = 0
    this.state.programTag = null
  }
}

export const LiveSessionStoreContext = createStoreContext<LiveSessionStore>()

export function useLiveSessionStore() {
  return useStoreContext(LiveSessionStoreContext)
}
